import * as React from 'react';
import * as Sentry from '@sentry/browser';

import { urls } from 'shared/entities/domain';
import { ComponentType } from 'shared/entities/components/Component';
import TypedTransComponent from 'shared/components/TypedTransComponent';
import { useRouterStore } from 'stores';
import Typography from 'shared/newComponents/Typography';
import { Button } from 'shared/newComponents/buttons';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { ButtonSize } from 'shared/newEntities/components/Button';

import ErrorBoundary, { ErrorBoundaryData } from '../ErrorBoundary';
import TransComp from '../TransComp';

import './SentryErrorBoundary.modules.scss';

type Props = {
  children?: React.ReactNode;
  ErrorComponent?: React.ComponentType<ErrorBoundaryData>;
};

const DefaultErrorComponent: React.FC<ErrorBoundaryData> = ({ error }) => {
  const routerStore = useRouterStore();
  const handleMain = React.useCallback(() => {
    routerStore.changeToAuthDomain({ path: urls.ROOT.mask });
  }, []);

  const errorInfo = React.useMemo(() => {
    if (error instanceof SyntaxError) {
      return (
        <TransComp>
          {(t) => t('SentryErrorBoundary.info', { ns: 'components' })}
        </TransComp>
      );
    }
    return null;
  }, [error]);

  return (
    <div styleName="error">
      <div styleName="error__content">
        <Typography
          fontType={TypographyType.title}
          fontSize={TypographySize.xs}
          fontWeight={TypographyWeight.bold}
          fontColor={TypographyColor.secondary}
        >
          <TypedTransComponent
            ns="components"
            i18nKey="SentryErrorBoundary.content"
          >
            Произошла неизвестная ошибка
            <Button
              type={ComponentType.button}
              size={ButtonSize.m}
              styleName="error__button"
              onClick={handleMain}
            >
              Перейти на главную
            </Button>
          </TypedTransComponent>
        </Typography>
        {errorInfo && (
          <Typography
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontWeight={TypographyWeight.heavy}
            fontColor={TypographyColor.secondary}
            styleName="error__info"
          >
            {errorInfo}
          </Typography>
        )}
      </div>
    </div>
  );
};

const SentryErrorBoundary: React.FC<Props> = ({
  children,
  ErrorComponent: CustomErrorComponent
}: Props) => {
  const onError = React.useCallback((error: Error, info: React.ErrorInfo) => {
    // eslint-disable-next-line no-console
    console.log('caught', error, info);
    Sentry.withScope((scope) => {
      scope.setExtras({ componentStack: info.componentStack });
      Sentry.captureException(error);
    });
  }, []);

  return (
    <ErrorBoundary
      onError={onError}
      ErrorComponent={CustomErrorComponent ?? DefaultErrorComponent}
    >
      {children}
    </ErrorBoundary>
  );
};

export default SentryErrorBoundary;
